import { X } from 'lucide-react';

import { useTranslation } from 'react-i18next';
interface Chat {
  id: string;
  title: string;
  created: string;
}

interface ChatHistoryPanelProps {
  show: boolean;
  chats: Chat[];
  currentChatId: string | null;
  onClose: () => void;
  onChatSelect: (chatId: string) => void;
  onNewChat: () => void;
  canCreateNewChat: boolean;
}

export function ChatHistoryPanel({
  show,
  chats,
  currentChatId,
  onClose,
  onChatSelect,
  onNewChat,
  canCreateNewChat,
}: ChatHistoryPanelProps) {
  const { t } = useTranslation('chat');
  return (
    <>
      {/* Overlay for closing on outside click */}
      {show && (
        <div
          className="fixed inset-0 z-10"
          onClick={onClose}
        />
      )}
      <div
        className={`
          fixed right-[16px] top-[16px] w-[280px] h-[calc(100vh-32px)] bg-white
          transform transition-transform duration-300 z-20
          rounded-[16px]
          flex flex-col
          ${show ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
      <div className="p-[16px] flex flex-col gap-[16px] flex-1 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-[8px]">
          {/* Chat icon - 24x24px with chat bubble and dots */}
          <div className="relative w-[24px] h-[24px] flex-shrink-0">
            {/* Chat bubble outline - 14px */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[14px] h-[14px]">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 15 15">
                <path
                  clipRule="evenodd"
                  d="M12.4499 12.449C10.3106 14.5885 7.14279 15.0508 4.55042 13.8519C4.16772 13.6978 3.85397 13.5733 3.55569 13.5733C2.72486 13.5782 1.69073 14.3838 1.15326 13.847C0.615797 13.3094 1.422 12.2745 1.422 11.4386C1.422 11.1403 1.3024 10.8322 1.14834 10.4487C-0.051112 7.85677 0.411785 4.6879 2.5511 2.54905C5.28205 -0.182901 9.71897 -0.182901 12.4499 2.54835C15.1858 5.28453 15.1809 9.71771 12.4499 12.449Z"
                  fillRule="evenodd"
                  stroke="#565858"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {/* Dot 1 */}
            <div className="absolute left-[calc(50%+2.761px)] top-[calc(50%+0.289px)] -translate-x-1/2 -translate-y-1/2 w-[0.7px] h-[0.7px]">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 2 2">
                <path d="M0.75 0.75H0.7563" stroke="#565858" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
              </svg>
            </div>
            {/* Dot 2 */}
            <div className="absolute left-[calc(50%-0.046px)] top-[calc(50%+0.289px)] -translate-x-1/2 -translate-y-1/2 w-[0.7px] h-[0.7px]">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 2 2">
                <path d="M0.75 0.75H0.7563" stroke="#565858" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
              </svg>
            </div>
            {/* Dot 3 */}
            <div className="absolute left-[calc(50%-2.852px)] top-[calc(50%+0.289px)] -translate-x-1/2 -translate-y-1/2 w-[0.7px] h-[0.7px]">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 2 2">
                <path d="M0.75 0.75H0.7563" stroke="#565858" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
              </svg>
            </div>
          </div>
          <span className="font-manrope font-semibold text-[14px] leading-[20px] text-[#0d0d0d] flex-1">
            {t('history.title')}
          </span>
          <button
            onClick={onClose}
            className="w-[24px] h-[24px] flex items-center justify-center hover:opacity-70 transition-opacity"
          >
            <X className="w-[14px] h-[14px] text-[#565858]" />
          </button>
        </div>

        {/* New Chat Button - Under header */}
        <button
          onClick={onNewChat}
          disabled={!canCreateNewChat}
          className={`
            flex items-center gap-[8px] px-[16px] py-[12px] rounded-[8px] w-full
            transition-colors
            ${canCreateNewChat
              ? 'hover:bg-white cursor-pointer'
              : 'opacity-60 cursor-not-allowed'
            }
          `}
        >
          <div className="relative w-[24px] h-[24px] flex-shrink-0">
            <div className="absolute left-[4px] top-[4px] w-[16px] h-[16px]">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 17 17">
                <path d="M0.5 8.5H16.5M8.5 16.5V8.5L8.5 0.5" stroke="#565858" strokeLinecap="round" />
              </svg>
            </div>
          </div>
          <span className="font-manrope font-semibold text-[14px] leading-[20px] text-[#565858]">
            {t('history.newChat')}
          </span>
        </button>

        {/* Chat list */}
        <div className="flex flex-col gap-[8px]">
          {chats.filter(chat => chat != null).map(chat => (
            <button
              key={chat.id}
              onClick={() => onChatSelect(chat.id)}
              className={`
                flex items-center gap-[16px] px-[16px] py-[8px] rounded-[8px] w-full
                text-left
                ${currentChatId === chat.id ? 'bg-[#eeeeee]' : 'hover:bg-white'}
                transition-colors
              `}
            >
              <span className="font-manrope font-semibold text-[14px] leading-[20px] text-[#0d0d0d] truncate">
                {chat.title || t('history.chatDate', { date: new Date(chat.created).toLocaleDateString('ru-RU') })}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
    </>
  );
}
